/**
 * useProjectIntensityTiles — fetch the Phase 4b intensity-tile manifest.
 *
 * Returns null (not an error) when the backend responds 404 — tiles
 * haven't been built for this project yet; caller should fall back to
 * the polygon layer.
 */

import { useQuery } from "@tanstack/react-query";
import { fetchProjectIntensityTiles } from "../../../api/projects";
import { useAuth } from "../../../auth/AuthProvider";
import type { IntensityTileResponse } from "../../model/domain/types";

export function useProjectIntensityTiles(
  projectId: string | undefined,
  enabled = true,
) {
  const { token } = useAuth();
  return useQuery<IntensityTileResponse | null>({
    queryKey: ["intensity-tiles", projectId],
    queryFn: () => fetchProjectIntensityTiles(projectId!, token!),
    enabled: Boolean(projectId) && Boolean(token) && enabled,
    // Tiles are static for a project — same cache profile as airsheds.
    staleTime: 60 * 60_000,
    gcTime: 2 * 60 * 60_000,
    retry: 0, // 404 is informative, don't hammer
  });
}
